import { useEffect, useMemo, useRef, useState } from "react";
import {
  Trash2,
  Upload,
  Pencil,
  FolderPlus,
  Image as ImageIcon,
  Folder,
  Check,
  CheckSquare,
  GripVertical,
  X as XIcon,
  MoveRight,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api, API_BASE } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";

/**
 * Interpolate a translation template like ``"{n} photos"`` with pluralization.
 * ``tSection`` should expose ``photoCount`` (singular) and optionally
 * ``photoCountPlural``. Falls back to the singular template when the plural
 * variant isn't defined.
 */
function formatPhotoCount(n, tSection) {
  const template = n === 1 ? tSection.photoCount : tSection.photoCountPlural || tSection.photoCount;
  return (template || "{n}").replace("{n}", String(n));
}

function formatSelectedCount(n, tSection) {
  const template = n === 1 ? tSection.selectedCount : tSection.selectedCountPlural || tSection.selectedCount;
  return (template || "{n}").replace("{n}", String(n));
}

function formatBulkMoveSuccess(n, tSection) {
  const template = n === 1 ? tSection.bulkMoveSuccess : tSection.bulkMoveSuccessPlural || tSection.bulkMoveSuccess;
  return (template || "{n}").replace("{n}", String(n));
}

/**
 * A single album card that participates in drag-and-drop reordering.
 *
 * ``@dnd-kit/sortable``'s ``useSortable`` gives us a stable ref plus the
 * transform CSS to apply while dragging. The default (non-default) album
 * cards have a grip handle in the top-right that owns the drag listeners —
 * the rest of the card remains clickable/tap-able for the Edit and Delete
 * buttons.
 *
 * The default "General" album gets a static, disabled sortable so the
 * position it takes in the array is preserved on the client while the
 * server always coerces its ``order`` back to 0 on save.
 */
function SortableAlbumCard({
  album,
  lang,
  tAlbums,
  onEdit,
  onDelete,
  disabled,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: album.id, disabled: disabled || album.is_default });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    // While being dragged, lift the card slightly and boost the shadow so the
    // move is visually obvious to the admin even on a plain white background.
    zIndex: isDragging ? 20 : "auto",
    opacity: isDragging ? 0.85 : 1,
  };

  const count = album.photo_count || 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`admin-albums-card-${album.id}`}
      className={
        "relative rounded-2xl ring-1 ring-black/5 bg-card overflow-hidden shadow-sm transition-shadow " +
        (isDragging ? "shadow-lg ring-primary/40" : "")
      }
    >
      {/* Drag handle: only for non-default albums. The default album is
          pinned to the first slot server-side so a grip here would just
          confuse admins. */}
      {!album.is_default && (
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="absolute right-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/85 text-muted-foreground shadow-sm backdrop-blur cursor-grab active:cursor-grabbing focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          aria-label={tAlbums.dragHandle}
          data-testid={`admin-albums-drag-${album.id}`}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      )}

      <div className="relative aspect-video bg-muted">
        {album.cover_url ? (
          <img
            src={album.cover_url}
            alt=""
            className="h-full w-full object-cover"
            draggable={false}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            <ImageIcon className="h-8 w-8 opacity-40" />
          </div>
        )}
        {album.is_default && (
          <Badge className="absolute left-2 top-2" variant="secondary">
            {tAlbums.defaultBadge}
          </Badge>
        )}
      </div>

      <div className="p-3 space-y-2">
        <div>
          <p className="font-serif-display text-base font-semibold truncate">
            {lang === "es" ? album.name_es : album.name_en}
          </p>
          <p className="text-[11px] text-muted-foreground">
            {formatPhotoCount(count, tAlbums)}
          </p>
          {(lang === "es" ? album.description_es : album.description_en) && (
            <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
              {lang === "es" ? album.description_es : album.description_en}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onEdit(album)}
            data-testid={`admin-albums-edit-${album.id}`}
          >
            <Pencil className="mr-1 h-3.5 w-3.5" />
            {tAlbums.editAlbum}
          </Button>
          {!album.is_default && (
            <Button
              size="sm"
              variant="destructive"
              onClick={() => onDelete(album)}
              data-testid={`admin-albums-delete-${album.id}`}
            >
              <Trash2 className="mr-1 h-3.5 w-3.5" />
              {tAlbums.delete}
            </Button>
          )}
        </div>
        {album.is_default && (
          <p className="text-[10px] text-muted-foreground">{tAlbums.defaultLocked}</p>
        )}
      </div>
    </div>
  );
}

export default function AdminPhotos() {
  const { t, lang } = useI18n();
  const tPhotos = t.admin.photosForm;
  const tAlbums = t.admin.albumsForm;

  const [photos, setPhotos] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(true);
  const [loadingAlbums, setLoadingAlbums] = useState(true);

  // ---- Upload form state ----
  const [caption_es, setCaptionEs] = useState("");
  const [caption_en, setCaptionEn] = useState("");
  const [uploadAlbumId, setUploadAlbumId] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // ---- Selection mode (bulk-move) ----
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkTargetAlbumId, setBulkTargetAlbumId] = useState("");
  const [bulkMoving, setBulkMoving] = useState(false);

  // ---- Photo edit dialog state ----
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [savingPhoto, setSavingPhoto] = useState(false);

  // ---- Photo delete confirmation ----
  const [confirmDeletePhoto, setConfirmDeletePhoto] = useState(null);

  // ---- Album create/edit state ----
  const [showAlbumDialog, setShowAlbumDialog] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState(null);
  const [albumForm, setAlbumForm] = useState({
    name_es: "",
    name_en: "",
    description_es: "",
    description_en: "",
    order: 100,
    cover_photo_id: "",
  });
  const [savingAlbum, setSavingAlbum] = useState(false);

  // ---- Album delete confirmation ----
  const [confirmDeleteAlbum, setConfirmDeleteAlbum] = useState(null);

  const albumsById = useMemo(
    () => Object.fromEntries(albums.map((a) => [a.id, a])),
    [albums]
  );
  const defaultAlbum = useMemo(() => albums.find((a) => a.is_default) || null, [albums]);

  // ------------------------------------------------------------------
  //  Data loading
  // ------------------------------------------------------------------
  const reloadAll = async () => {
    setLoadingPhotos(true);
    setLoadingAlbums(true);
    try {
      const [pRes, aRes] = await Promise.all([
        api.get("/photos"),
        api.get("/admin/albums"),
      ]);
      setPhotos(pRes.data || []);
      setAlbums(aRes.data || []);
      if (!uploadAlbumId && (aRes.data || []).length > 0) {
        setUploadAlbumId(aRes.data[0].id);
      }
    } catch (_) {
      toast.error(t.common.error);
    } finally {
      setLoadingPhotos(false);
      setLoadingAlbums(false);
    }
  };

  useEffect(() => {
    reloadAll();
    // Mount-once fetch. reloadAll closes over `uploadAlbumId` but we only
    // set it inside when it's empty, so re-running on change would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------------------------------------------------
  //  Upload
  // ------------------------------------------------------------------
  const onUpload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error(t.common.required);
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("caption_es", caption_es);
      form.append("caption_en", caption_en);
      if (uploadAlbumId) form.append("album_id", uploadAlbumId);
      const token =
        (typeof localStorage !== "undefined" && localStorage.getItem("ncl_admin_token")) || "";
      const res = await fetch(`${API_BASE}/admin/photos/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) throw new Error("upload failed");
      toast.success(t.admin.saved);
      setCaptionEs("");
      setCaptionEn("");
      if (fileRef.current) fileRef.current.value = "";
      reloadAll();
    } catch (_) {
      toast.error(t.common.error);
    } finally {
      setUploading(false);
    }
  };

  // ------------------------------------------------------------------
  //  Photo edit + delete
  // ------------------------------------------------------------------
  const openEditPhoto = (photo) => setEditingPhoto({ ...photo });

  const savePhotoEdit = async () => {
    if (!editingPhoto) return;
    setSavingPhoto(true);
    try {
      await api.put(`/admin/photos/${editingPhoto.id}`, {
        image_url: editingPhoto.image_url,
        caption_es: editingPhoto.caption_es || "",
        caption_en: editingPhoto.caption_en || "",
        album_id: editingPhoto.album_id || (defaultAlbum && defaultAlbum.id) || null,
      });
      toast.success(t.admin.saved);
      setEditingPhoto(null);
      reloadAll();
    } catch (_) {
      toast.error(t.common.error);
    } finally {
      setSavingPhoto(false);
    }
  };

  const confirmDeletePhotoNow = async () => {
    if (!confirmDeletePhoto) return;
    try {
      await api.delete(`/admin/photos/${confirmDeletePhoto.id}`);
      toast.success(t.admin.deleted);
      setConfirmDeletePhoto(null);
      reloadAll();
    } catch (_) {
      toast.error(t.common.error);
    }
  };

  // ------------------------------------------------------------------
  //  Selection mode & bulk-move
  // ------------------------------------------------------------------
  const enterSelectMode = () => {
    setSelectMode(true);
    setSelectedIds(new Set());
    setBulkTargetAlbumId("");
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
    setBulkTargetAlbumId("");
  };

  /** Toggle whether ``id`` is in the current selection. */
  const togglePhotoSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllPhotos = () => setSelectedIds(new Set(photos.map((p) => p.id)));

  const clearSelection = () => setSelectedIds(new Set());

  const doBulkMove = async () => {
    if (!bulkTargetAlbumId || selectedIds.size === 0) return;
    setBulkMoving(true);
    try {
      const { data } = await api.post("/admin/photos/bulk-move", {
        photo_ids: Array.from(selectedIds),
        album_id: bulkTargetAlbumId,
      });
      toast.success(formatBulkMoveSuccess(data?.moved ?? selectedIds.size, tPhotos));
      exitSelectMode();
      reloadAll();
    } catch (err) {
      toast.error(String(err?.response?.data?.detail || t.common.error));
    } finally {
      setBulkMoving(false);
    }
  };

  // ------------------------------------------------------------------
  //  Album create/edit/delete
  // ------------------------------------------------------------------
  const openNewAlbum = () => {
    setEditingAlbum(null);
    setAlbumForm({
      name_es: "",
      name_en: "",
      description_es: "",
      description_en: "",
      order: 100,
      cover_photo_id: "",
    });
    setShowAlbumDialog(true);
  };

  const openEditAlbum = (album) => {
    setEditingAlbum(album);
    setAlbumForm({
      name_es: album.name_es || "",
      name_en: album.name_en || "",
      description_es: album.description_es || "",
      description_en: album.description_en || "",
      order: album.order ?? 100,
      cover_photo_id: album.cover_photo_id || "",
    });
    setShowAlbumDialog(true);
  };

  const saveAlbum = async () => {
    const payload = {
      name_es: albumForm.name_es.trim(),
      name_en: albumForm.name_en.trim(),
      description_es: albumForm.description_es.trim(),
      description_en: albumForm.description_en.trim(),
      order: Number(albumForm.order) || 100,
      cover_photo_id: albumForm.cover_photo_id || null,
    };
    if (!payload.name_es && !payload.name_en) {
      toast.error(t.common.required);
      return;
    }
    setSavingAlbum(true);
    try {
      if (editingAlbum) {
        await api.put(`/admin/albums/${editingAlbum.id}`, payload);
      } else {
        await api.post("/admin/albums", payload);
      }
      toast.success(t.admin.saved);
      setShowAlbumDialog(false);
      setEditingAlbum(null);
      reloadAll();
    } catch (err) {
      toast.error(String(err?.response?.data?.detail || t.common.error));
    } finally {
      setSavingAlbum(false);
    }
  };

  const confirmDeleteAlbumNow = async () => {
    if (!confirmDeleteAlbum) return;
    try {
      const { data } = await api.delete(`/admin/albums/${confirmDeleteAlbum.id}`);
      if (data?.moved_photos > 0) {
        toast.success(
          lang === "es"
            ? `Album eliminado. ${data.moved_photos} foto${data.moved_photos === 1 ? "" : "s"} movidas a General.`
            : `Album deleted. ${data.moved_photos} photo${data.moved_photos === 1 ? "" : "s"} moved to General.`
        );
      } else {
        toast.success(t.admin.deleted);
      }
      setConfirmDeleteAlbum(null);
      reloadAll();
    } catch (err) {
      toast.error(String(err?.response?.data?.detail || t.common.error));
    }
  };

  const albumCoverCandidates = useMemo(() => {
    if (!editingAlbum) return [];
    return photos.filter((p) => p.album_id === editingAlbum.id);
  }, [photos, editingAlbum]);

  // ------------------------------------------------------------------
  //  Drag-and-drop reordering (@dnd-kit)
  // ------------------------------------------------------------------
  // Sensors: PointerSensor with a small activation distance so click-through
  // on the album cards (Edit/Delete buttons) still works — dragging only
  // starts once the cursor has actually moved 5 pixels. TouchSensor uses a
  // short press-and-hold so scrolling still works on mobile.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = albums.findIndex((a) => a.id === active.id);
    const newIndex = albums.findIndex((a) => a.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const nextAlbums = arrayMove(albums, oldIndex, newIndex);
    // Optimistic UI: update state immediately, revert on failure.
    setAlbums(nextAlbums);
    try {
      await api.put("/admin/albums/reorder", {
        album_ids: nextAlbums.map((a) => a.id),
      });
      toast.success(tAlbums.reorderSuccess);
      // Refresh from server so the ``order`` field on each card is up to
      // date; this also handles the case where the server pinned "General"
      // back to position 0 even if the admin dragged it elsewhere.
      reloadAll();
    } catch (err) {
      // Revert optimistic update on failure.
      setAlbums(albums);
      toast.error(String(err?.response?.data?.detail || t.common.error));
    }
  };

  // ------------------------------------------------------------------
  //  Render
  // ------------------------------------------------------------------
  const selectedCount = selectedIds.size;

  return (
    <div className="space-y-4" data-testid="admin-photos-page">
      <h1 className="font-serif-display text-2xl font-semibold">{t.admin.photos}</h1>

      <Tabs defaultValue="photos" className="w-full">
        <TabsList className="grid w-full grid-cols-2" data-testid="admin-photos-tabs">
          <TabsTrigger value="photos" data-testid="admin-photos-tab-photos">
            <ImageIcon className="mr-1 h-4 w-4" />
            {tPhotos.photosTab}
          </TabsTrigger>
          <TabsTrigger value="albums" data-testid="admin-photos-tab-albums">
            <Folder className="mr-1 h-4 w-4" />
            {tPhotos.albumsTab}
          </TabsTrigger>
        </TabsList>

        {/* ============================================================
             PHOTOS TAB
        ============================================================ */}
        <TabsContent value="photos" className="space-y-4 mt-4">
          <Card className="rounded-2xl ring-1 ring-black/5">
            <CardContent className="p-4">
              <form className="space-y-3" onSubmit={onUpload} data-testid="admin-photos-form">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">{tPhotos.file}</Label>
                  <Input ref={fileRef} type="file" accept="image/*" data-testid="admin-photos-file" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">{tPhotos.caption_es}</Label>
                    <Input
                      value={caption_es}
                      onChange={(e) => setCaptionEs(e.target.value)}
                      data-testid="admin-photos-caption-es"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">{tPhotos.caption_en}</Label>
                    <Input
                      value={caption_en}
                      onChange={(e) => setCaptionEn(e.target.value)}
                      data-testid="admin-photos-caption-en"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">{tPhotos.album}</Label>
                  <Select value={uploadAlbumId} onValueChange={setUploadAlbumId}>
                    <SelectTrigger data-testid="admin-photos-upload-album">
                      <SelectValue placeholder={tPhotos.selectAlbumPlaceholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {albums.map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {lang === "es" ? a.name_es : a.name_en}
                          {a.is_default ? ` \u2022 ${tAlbums.defaultBadge}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground">{tPhotos.albumHint}</p>
                </div>
                <Button type="submit" disabled={uploading || !uploadAlbumId} data-testid="admin-photos-upload">
                  <Upload className="mr-1 h-4 w-4" />
                  {uploading ? "..." : tPhotos.upload}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* -------- Selection-mode toolbar (only when photos exist) -------- */}
          {photos.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {selectMode ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={exitSelectMode}
                    data-testid="admin-photos-exit-select"
                  >
                    <XIcon className="mr-1 h-4 w-4" />
                    {tPhotos.exitSelect}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={selectAllPhotos}
                    data-testid="admin-photos-select-all"
                  >
                    {tPhotos.selectAll}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearSelection}
                    data-testid="admin-photos-clear-selection"
                  >
                    {tPhotos.clearSelection}
                  </Button>
                  <p className="text-xs text-muted-foreground ml-auto" data-testid="admin-photos-selected-count">
                    {formatSelectedCount(selectedCount, tPhotos)}
                  </p>
                </>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={enterSelectMode}
                  data-testid="admin-photos-enter-select"
                >
                  <CheckSquare className="mr-1 h-4 w-4" />
                  {tPhotos.selectMode}
                </Button>
              )}
            </div>
          )}

          {loadingPhotos ? (
            <Card className="rounded-2xl border-dashed bg-white/60">
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                {t.common.loading}
              </CardContent>
            </Card>
          ) : photos.length === 0 ? (
            <Card className="rounded-2xl border-dashed bg-white/60">
              <CardContent
                className="py-10 text-center text-sm text-muted-foreground"
                data-testid="admin-photos-empty"
              >
                {t.admin.noItems}
              </CardContent>
            </Card>
          ) : (
            <div
              className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 pb-24"
              data-testid="admin-photos-grid"
            >
              {photos.map((p) => {
                const album = albumsById[p.album_id];
                const albumName = album
                  ? lang === "es"
                    ? album.name_es
                    : album.name_en
                  : "\u2014";
                const isSelected = selectedIds.has(p.id);
                const handleTileClick = (e) => {
                  if (!selectMode) return;
                  e.preventDefault();
                  togglePhotoSelected(p.id);
                };
                return (
                  <div
                    key={p.id}
                    onClick={handleTileClick}
                    role={selectMode ? "checkbox" : undefined}
                    aria-checked={selectMode ? isSelected : undefined}
                    tabIndex={selectMode ? 0 : -1}
                    onKeyDown={(e) => {
                      if (!selectMode) return;
                      if (e.key === " " || e.key === "Enter") {
                        e.preventDefault();
                        togglePhotoSelected(p.id);
                      }
                    }}
                    className={
                      "relative group aspect-square overflow-hidden rounded-xl bg-muted ring-1 transition " +
                      (isSelected
                        ? "ring-primary ring-2 shadow-md scale-[0.98]"
                        : "ring-black/5") +
                      (selectMode ? " cursor-pointer" : "")
                    }
                    data-testid={`admin-photos-item-${p.id}`}
                  >
                    <img src={p.image_url} alt="" className="h-full w-full object-cover" draggable={false} />

                    {/* Non-select actions: pencil / trash. Hidden while in
                        select mode so the whole tile becomes a checkbox. */}
                    {!selectMode && (
                      <div className="absolute inset-x-1 top-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          size="icon"
                          variant="secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditPhoto(p);
                          }}
                          className="h-7 w-7 shadow"
                          data-testid={`admin-photos-edit-${p.id}`}
                          aria-label={tPhotos.editPhoto}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmDeletePhoto(p);
                          }}
                          className="ml-auto h-7 w-7"
                          data-testid={`admin-photos-delete-${p.id}`}
                          aria-label={tPhotos.deletePhoto}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}

                    {/* Selection checkbox overlay */}
                    {selectMode && (
                      <div
                        className={
                          "absolute right-1.5 top-1.5 h-6 w-6 rounded-full flex items-center justify-center ring-2 " +
                          (isSelected
                            ? "bg-primary ring-primary text-primary-foreground"
                            : "bg-white/85 ring-white/70 text-muted-foreground")
                        }
                        data-testid={`admin-photos-check-${p.id}`}
                        aria-hidden="true"
                      >
                        {isSelected && <Check className="h-4 w-4" />}
                      </div>
                    )}

                    <div className="absolute inset-x-0 bottom-0 bg-black/55 px-2 py-1 text-[10px] leading-tight text-white">
                      <p className="truncate font-medium">
                        {tPhotos.inAlbum} {albumName}
                      </p>
                      {(lang === "es" ? p.caption_es : p.caption_en) && (
                        <p className="truncate opacity-90">
                          {lang === "es" ? p.caption_es : p.caption_en}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ============================================================
             ALBUMS TAB
        ============================================================ */}
        <TabsContent value="albums" className="space-y-4 mt-4">
          <Card className="rounded-2xl ring-1 ring-black/5">
            <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
              <div>
                <p className="font-serif-display text-lg font-semibold">{tAlbums.pageTitle}</p>
                <p className="text-xs text-muted-foreground">{tAlbums.pageSubtitle}</p>
              </div>
              <Button onClick={openNewAlbum} data-testid="admin-albums-new-button">
                <FolderPlus className="mr-1 h-4 w-4" />
                {tAlbums.newAlbum}
              </Button>
            </CardContent>
          </Card>

          {albums.length > 1 && (
            <p className="px-1 text-[11px] text-muted-foreground inline-flex items-center gap-1">
              <GripVertical className="h-3 w-3" />
              {tAlbums.dragHint}
            </p>
          )}

          {loadingAlbums ? (
            <Card className="rounded-2xl border-dashed bg-white/60">
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                {t.common.loading}
              </CardContent>
            </Card>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={onDragEnd}
            >
              <SortableContext
                items={albums.map((a) => a.id)}
                strategy={rectSortingStrategy}
              >
                <div
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
                  data-testid="admin-albums-grid"
                >
                  {albums.map((a) => (
                    <SortableAlbumCard
                      key={a.id}
                      album={a}
                      lang={lang}
                      tAlbums={tAlbums}
                      onEdit={openEditAlbum}
                      onDelete={setConfirmDeleteAlbum}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </TabsContent>
      </Tabs>

      {/* ==================================================================
          Floating bulk-move action bar
      ================================================================== */}
      {selectMode && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 border-t border-black/10 bg-background/95 backdrop-blur px-4 py-3 shadow-[0_-8px_20px_-8px_rgba(0,0,0,0.15)]"
          data-testid="admin-photos-bulk-bar"
        >
          <div className="mx-auto flex max-w-3xl flex-col gap-2 sm:flex-row sm:items-center">
            <p className="text-xs sm:text-sm font-medium">
              {selectedCount === 0
                ? tPhotos.selectionHint
                : formatSelectedCount(selectedCount, tPhotos)}
            </p>
            <div className="flex flex-1 items-center gap-2 sm:justify-end">
              <div className="flex-1 sm:flex-none sm:min-w-[220px]">
                <Select
                  value={bulkTargetAlbumId}
                  onValueChange={setBulkTargetAlbumId}
                  disabled={selectedCount === 0 || bulkMoving}
                >
                  <SelectTrigger data-testid="admin-photos-bulk-target">
                    <SelectValue placeholder={tPhotos.moveToPlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {albums.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {lang === "es" ? a.name_es : a.name_en}
                        {a.is_default ? ` \u2022 ${tAlbums.defaultBadge}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={doBulkMove}
                disabled={selectedCount === 0 || !bulkTargetAlbumId || bulkMoving}
                data-testid="admin-photos-bulk-move"
              >
                <MoveRight className="mr-1 h-4 w-4" />
                {tPhotos.moveAction}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ==================================================================
          Photo edit dialog
      ================================================================== */}
      <Dialog open={!!editingPhoto} onOpenChange={(v) => !v && setEditingPhoto(null)}>
        <DialogContent data-testid="admin-photo-edit-dialog">
          <DialogHeader>
            <DialogTitle>{tPhotos.editPhoto}</DialogTitle>
          </DialogHeader>
          {editingPhoto && (
            <div className="space-y-3">
              <div className="aspect-video overflow-hidden rounded-lg bg-muted">
                <img src={editingPhoto.image_url} alt="" className="h-full w-full object-cover" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">{tPhotos.album}</Label>
                <Select
                  value={editingPhoto.album_id || ""}
                  onValueChange={(v) => setEditingPhoto((p) => ({ ...p, album_id: v }))}
                >
                  <SelectTrigger data-testid="admin-photo-edit-album">
                    <SelectValue placeholder={tPhotos.selectAlbumPlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {albums.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {lang === "es" ? a.name_es : a.name_en}
                        {a.is_default ? ` \u2022 ${tAlbums.defaultBadge}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">{tPhotos.caption_es}</Label>
                  <Input
                    value={editingPhoto.caption_es || ""}
                    onChange={(e) => setEditingPhoto((p) => ({ ...p, caption_es: e.target.value }))}
                    data-testid="admin-photo-edit-caption-es"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">{tPhotos.caption_en}</Label>
                  <Input
                    value={editingPhoto.caption_en || ""}
                    onChange={(e) => setEditingPhoto((p) => ({ ...p, caption_en: e.target.value }))}
                    data-testid="admin-photo-edit-caption-en"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPhoto(null)}>
              {tAlbums.cancel}
            </Button>
            <Button onClick={savePhotoEdit} disabled={savingPhoto} data-testid="admin-photo-edit-save">
              {tPhotos.savePhoto}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================================================================
          Album create/edit dialog
      ================================================================== */}
      <Dialog open={showAlbumDialog} onOpenChange={setShowAlbumDialog}>
        <DialogContent data-testid="admin-album-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingAlbum ? tAlbums.editAlbum : tAlbums.newAlbum}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">{tAlbums.nameEs}</Label>
                <Input
                  value={albumForm.name_es}
                  onChange={(e) => setAlbumForm((f) => ({ ...f, name_es: e.target.value }))}
                  data-testid="admin-album-name-es"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">{tAlbums.nameEn}</Label>
                <Input
                  value={albumForm.name_en}
                  onChange={(e) => setAlbumForm((f) => ({ ...f, name_en: e.target.value }))}
                  data-testid="admin-album-name-en"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">{tAlbums.descEs}</Label>
                <Textarea
                  rows={2}
                  value={albumForm.description_es}
                  onChange={(e) => setAlbumForm((f) => ({ ...f, description_es: e.target.value }))}
                  data-testid="admin-album-desc-es"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">{tAlbums.descEn}</Label>
                <Textarea
                  rows={2}
                  value={albumForm.description_en}
                  onChange={(e) => setAlbumForm((f) => ({ ...f, description_en: e.target.value }))}
                  data-testid="admin-album-desc-en"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">{tAlbums.order}</Label>
                <Input
                  type="number"
                  value={albumForm.order}
                  onChange={(e) => setAlbumForm((f) => ({ ...f, order: e.target.value }))}
                  data-testid="admin-album-order"
                />
                <p className="text-[11px] text-muted-foreground">{tAlbums.orderHint}</p>
              </div>
              {editingAlbum && albumCoverCandidates.length > 0 && (
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">{tAlbums.coverPickManual}</Label>
                  <Select
                    value={albumForm.cover_photo_id || "__auto__"}
                    onValueChange={(v) =>
                      setAlbumForm((f) => ({
                        ...f,
                        cover_photo_id: v === "__auto__" ? "" : v,
                      }))
                    }
                  >
                    <SelectTrigger data-testid="admin-album-cover-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__auto__">{tAlbums.coverAuto}</SelectItem>
                      {albumCoverCandidates.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {(lang === "es" ? p.caption_es : p.caption_en) || p.id.slice(0, 8)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAlbumDialog(false)}>
              {tAlbums.cancel}
            </Button>
            <Button onClick={saveAlbum} disabled={savingAlbum} data-testid="admin-album-save">
              {editingAlbum ? tAlbums.save : tAlbums.create}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================================================================
          Photo delete confirmation
      ================================================================== */}
      <AlertDialog open={!!confirmDeletePhoto} onOpenChange={(v) => !v && setConfirmDeletePhoto(null)}>
        <AlertDialogContent data-testid="admin-photo-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t.admin.confirmDelete}</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tAlbums.cancel}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeletePhotoNow}>{tPhotos.deletePhoto}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ==================================================================
          Album delete confirmation
      ================================================================== */}
      <AlertDialog open={!!confirmDeleteAlbum} onOpenChange={(v) => !v && setConfirmDeleteAlbum(null)}>
        <AlertDialogContent data-testid="admin-album-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{tAlbums.confirmDeleteTitle}</AlertDialogTitle>
            <AlertDialogDescription>{tAlbums.confirmDeleteBody}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tAlbums.cancel}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteAlbumNow}>
              {tAlbums.confirmDeleteCta}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
